import axios, { AxiosResponse } from 'axios';
import { ThunkDispatch } from 'redux-thunk';
import { pushToGTM } from '~/actions/gtmActions';
import { IOnboardingState, IState } from '~/sharedInterfaces/IState';
import { getGATrafficCookie } from '~/services/StateService';
import {
  addReferrerUrl,
  addSourceToUrl,
  getUrls,
  normalizeIdentifier,
  objectHasData,
} from '~/utils';
import { contentTypeJSON, handleError, handleSuccess, uploadFiles } from './submitActions';
import {
  setAccessToken,
  setClientId,
  setLoading,
  setSignupProcessNewCustomer,
} from './widgetContextActions';

interface ISignupResponse {
  redirectUrl: string;
  uid: string;
  newCustomer: boolean;
  accessToken: string;
  clientId: number;
}

const { SIGNUP_URL, ONBOARDING_URL } = getUrls();

const submitOnboarding = (newCustomer: boolean, filePaths?: string[]) => {
  return (dispatch: ThunkDispatch<any, any, any>, getState: () => IState) => {
    const { onboarding, widgetContext, signup } = getState();
    const { turnover, personalNumber, loanPurpose } = onboarding;
    const message = `Purpose: ${loanPurpose?.purpose || 'No purpose provided.'} Additional Info: ${
      loanPurpose?.additionalInfo || 'No additional info provided.'
    }`;
    const userIdentifier = signup.orgNumber || widgetContext.sessionId || '';

    const onboardingPayload = {
      message,
      sessionId: widgetContext.sessionId,
      newCustomer,
      personalNumber: personalNumber ? normalizeIdentifier(personalNumber) : personalNumber,
      files: filePaths,
      turnover,
      countryCode: widgetContext.settings.market,
      source: 'qred',
      partnerCode: widgetContext.settings.partnerCode,
    };

    axios
      .post(
        `${ONBOARDING_URL}/${normalizeIdentifier(userIdentifier)}`,
        onboardingPayload,
        contentTypeJSON
      )
      .then(() => {
        // TODO: Remove duplicate events when UX have confirmed that they're done with restructuring in Google Anayltics
        // The long term solution is to use the event structure where we have the `product` inside the payload, instead of the event name.
        // But we're not fully ready to remove the old event yet, so that's why we're sending "double" events right now.
        dispatch(
          pushToGTM({
            event: 'widget-loan-application-append-succeeded',
            NLC: newCustomer,
            'step-2-data': onboarding,
          })
        );
        dispatch(
          pushToGTM({
            event: '  ',
            product: widgetContext.settings.widgetType,
            NLC: newCustomer,
            'step-2-data': onboarding,
          })
        );
        dispatch(handleSuccess());
      })
      .catch((error) => {
        dispatch(
          pushToGTM({
            event: 'widget-loan-application-append-rejected',
          })
        );
        dispatch(
          pushToGTM({
            event: 'widget-application-append-rejected',
            product: widgetContext.settings.widgetType,
          })
        );
        dispatch(handleError(error));
      });
  };
};

const handleSubmitOnboardingFlow = (newCustomer: boolean) => {
  return (dispatch: ThunkDispatch<any, any, any>, getState: () => IState) => {
    const { onboarding, signup, widgetContext } = getState();
    const urlList = onboarding.documentUpload || [];
    const userIdentifier = signup.orgNumber || widgetContext.sessionId || '';

    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      bankIntegration,
      ...onboardingStateWithoutBankIntegration
    }: IOnboardingState = onboarding;

    if (urlList && urlList.length) {
      uploadFiles(urlList, userIdentifier, widgetContext.settings.widgetType).then((fileList) =>
        dispatch(submitOnboarding(newCustomer, fileList))
      );
    } else if (objectHasData(onboardingStateWithoutBankIntegration)) {
      dispatch(submitOnboarding(newCustomer));
    } else {
      dispatch(handleSuccess());
    }
  };
};

const submitSignUp = () => {
  return (dispatch: ThunkDispatch<any, any, any>, getState: () => IState) => {
    const { widgetContext, signup } = getState();

    const { homeAddress, ...rest } = signup;
    const formattedCompanyStreet =
      homeAddress?.street &&
      homeAddress?.houseNumber &&
      `${homeAddress.street} ${homeAddress.houseNumber} ${homeAddress?.houseNumberAddition || ''}`;
    const formatedPostCode = homeAddress?.postCode?.replace(/\s/g, '').toLowerCase();

    const payload = {
      ...rest,
      clientStreet: formattedCompanyStreet,
      clientCity: homeAddress?.city,
      clientPostCode: formatedPostCode,
      sessionId: widgetContext.sessionId,
      referenceUrl: addReferrerUrl(),
      landingUrl: addSourceToUrl(),
      countryCode: widgetContext.settings.market,
      source: 'qred',
      partnerCode: widgetContext.settings.partnerCode,
      gaTraffic: getGATrafficCookie(),
    };

    dispatch(
      pushToGTM({
        event: 'widget-loan-application-sent',
        payload,
      })
    );

    dispatch(
      pushToGTM({
        event: 'widget-application-sent',
        product: widgetContext.settings.widgetType,
      })
    );

    dispatch(
      pushToGTM({
        event: 'q_user_a_application_sent',
      })
    );

    axios
      .post(SIGNUP_URL, payload, contentTypeJSON)
      .then(({ data }: AxiosResponse<ISignupResponse>) => {
        const { newCustomer, accessToken, clientId } = data;
        dispatch(setSignupProcessNewCustomer(newCustomer));
        dispatch(setAccessToken(accessToken));
        dispatch(setClientId(clientId));
        dispatch(
          pushToGTM({
            event: 'widget-loan-application-succeeded',
            'step-1-data': signup,
          })
        );
        dispatch(
          pushToGTM({
            event: 'widget-application-succeeded',
            'step-1-data': signup,
            product: widgetContext.settings.widgetType,
          })
        );
        dispatch(
          pushToGTM({
            event: newCustomer
              ? 'widget-loan-application-is-nlc'
              : 'widget-loan-application-is-rlc',
          })
        );
        dispatch(
          pushToGTM({
            event: 'q_user_a_application_successful',
          })
        );
        dispatch(
          pushToGTM({
            event: newCustomer ? 'widget-application-is-nlc' : 'widget-application-is-rlc',
            product: widgetContext.settings.widgetType,
          })
        );
        dispatch(handleSubmitOnboardingFlow(newCustomer));
      })
      .catch((error) => {
        dispatch(
          pushToGTM({
            event: 'widget-loan-application-rejected',
          })
        );
        dispatch(
          pushToGTM({
            event: 'widget-application-rejected',
            product: widgetContext.settings.widgetType,
          })
        );
        dispatch(
          pushToGTM({
            event: 'q_user_a_application_rejected',
          })
        );
        dispatch(handleError(error));
      });
  };
};

// eslint-disable-next-line import/prefer-default-export
export const submitLoan = () => {
  return (dispatch: ThunkDispatch<any, any, any>, getState: () => IState) => {
    const { widgetContext } = getState();
    const { loading, signupCompleted, signupNewCustomer } = widgetContext;

    if (!loading && !signupCompleted) {
      dispatch(setLoading(true));
      dispatch(submitSignUp());
    } else if (!loading && signupCompleted) {
      dispatch(setLoading(true));
      dispatch(handleSubmitOnboardingFlow(!!signupNewCustomer));
    }
  };
};
